//--------------------------------------------------------------------//
//******************** agentToolsTab ***********************************//
//--------------------------------------------------------------------//
// Lists the agent's tool catalogs (system tools plus whatever MCP
// catalogs the server returns) on the left; the right side lists that
// catalog's tools with a checkbox to enable/disable each one. Groups are
// built from the GET response's own array entries ({ name, tools }) rather
// than a hardcoded list, so any additional catalog the backend adds later
// (e.g. another saved MCP server) shows up automatically.
import hljs from 'highlight.js';
import agentToolsTemplateUrl from './agent-tools.template.html';

const KNOWN_GROUP_LABELS = {
    system: 'System Tools',
    editor: 'MCP · Editor',
    admin: 'MCP · Admin'
};

// Formats an unrecognized catalog key into a readable label, e.g. "my_server" -> "My Server".
function titleCaseKey(key) {
    return String(key || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

angular.module('agentsTableModule')

.directive('agentToolsTab', ['AgentToolsService', function (AgentToolsService) {
    return {
        restrict: 'E',
        scope: {
            agent: '='
        },
        controller: ['$scope', '$sce', 'localizationService', function ($scope, $sce, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            $scope.groups = [];
            $scope.selectedGroupKey = null;
            $scope.loading = false;
            $scope.errorMessage = '';

            const agentId = $scope.agent._id;

            $scope.selectedGroup = function () {
                return $scope.groups.find((g) => g.key === $scope.selectedGroupKey) || null;
            };

            $scope.groupDetailOpen = false;
            $scope.selectGroup = function (key) {
                $scope.selectedGroupKey = key;
                $scope.groupDetailOpen = true;
            };

            $scope.enabledCount = function (group) {
                return group.tools.filter((t) => t.enabled).length;
            };

            // Fetches the agent's tool catalogs and builds the left-hand group
            // list from whatever { name, tools } entries the response array
            // actually contains.
            async function loadTools() {
                if (!agentId) return;
                $scope.loading = true;
                $scope.errorMessage = '';
                try {
                    const response = await AgentToolsService.getTools(agentId) || [];
                    $scope.groups = response
                        .map((group) => ({
                            key: group.name,
                            label: KNOWN_GROUP_LABELS[group.name] || titleCaseKey(group.name),
                            tools: group.tools || []
                        }))
                        .filter((group) => group.tools.length);

                    if (!$scope.groups.some((g) => g.key === $scope.selectedGroupKey)) {
                        $scope.selectedGroupKey = $scope.groups.length ? $scope.groups[0].key : null;
                    }
                } catch (err) {
                    console.error('Failed to load agent tools:', err);
                    $scope.errorMessage = err.message || 'Failed to load tools';
                } finally {
                    $scope.loading = false;
                    $scope.$applyAsync();
                }
            }
            loadTools();

            // Maps a UI catalog key to the API's :group param for the per-tool
            // routes — 'system' stays as-is, every other catalog (editor, admin,
            // ...) folds into the single flat 'mcp' allowlist (see api-tools.md).
            function apiGroupFor(groupKey) {
                return groupKey === 'system' ? 'system' : 'mcp';
            }

            // Toggles a single tool's enabled state via the per-tool enable/disable
            // routes. On failure, resyncs from the server instead of guessing at a
            // local revert.
            $scope.toggleTool = function (group, tool) {
                if (tool.saving) return;

                const wasEnabled = tool.enabled;
                const enabling = !wasEnabled;
                tool.enabled = enabling;
                tool.saving = true;
                $scope.errorMessage = '';

                const toggleCall = enabling
                    ? AgentToolsService.enableTool(agentId, apiGroupFor(group.key), tool.name)
                    : AgentToolsService.disableTool(agentId, apiGroupFor(group.key), tool.name);

                toggleCall
                    .catch((err) => {
                        console.error('Failed to update agent tools:', err);
                        $scope.errorMessage = err.message || 'Failed to update tool';
                        loadTools();
                    })
                    .finally(() => {
                        tool.saving = false;
                        $scope.$applyAsync();
                    });
            };

            // Expands/collapses a tool's schema card. Fetches the tool's full
            // input_schema only on first expand and caches the highlighted
            // markup on the tool object, so re-toggling doesn't re-fetch.
            $scope.toggleExpand = function (tool) {
                tool.expanded = !tool.expanded;
                if (!tool.expanded || tool.schemaHtml || tool.schemaLoading) return;

                tool.schemaLoading = true;
                tool.schemaError = '';

                AgentToolsService.getToolSchema(agentId, tool.name)
                    .then((result) => {
                        const json = JSON.stringify(result.input_schema || {}, null, 2);
                        const highlighted = hljs.highlight(json, { language: 'json' }).value;
                        tool.schemaHtml = $sce.trustAsHtml(highlighted);
                    })
                    .catch((err) => {
                        console.error('Failed to load tool schema:', err);
                        tool.schemaError = err.message || 'Failed to load schema';
                    })
                    .finally(() => {
                        tool.schemaLoading = false;
                        $scope.$applyAsync();
                    });
            };
        }],
        templateUrl: agentToolsTemplateUrl
    };
}]);
